import { useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { sceneUrl } from "../../lib/stb";

const Ball = () => (
  <svg viewBox="0 0 40 40" className="stb-ball h-full w-full drop-shadow-lg" aria-hidden>
    <circle cx="20" cy="20" r="18" fill="#fff" stroke="#0f172a" strokeWidth="2" />
    <path d="M20 11l7 5-3 8h-8l-3-8z" fill="#0f172a" />
    <path d="M20 2v9M27 16l9-3M24 24l6 8M16 24l-6 8M13 16l-9-3" stroke="#0f172a" strokeWidth="2" />
  </svg>
);

// squares: { [n]: {label, status} }; status: taken | held | mine | selected
export const StbBoard = ({ card, squares, selected = [], onPick, revealed, showOwners = true }) => {
  const [zoom, setZoom] = useState(1);
  const rows = card.squares / 10;
  const game = card.game || "spot";
  const dg = card.digits;
  const tag = (n) => (game === "squares" && dg ? `${dg.rows[Math.floor((n - 1) / 10)]}–${dg.cols[(n - 1) % 10]}` : game === "duck" ? `#${n}` : n);
  const cell = (n) => {
    const s = squares[n];
    const sel = selected.includes(n);
    const win = revealed && revealed.winner_square === n;
    const cls = win ? "bg-lime-400/70 ring-4 ring-lime-300" : sel ? "bg-lime-300/80 ring-2 ring-slate-900" : s ? "bg-slate-900/70" : "hover:bg-white/30 active:bg-lime-300/60";
    return (
      <button key={n} type="button" disabled={!onPick || (!!s && !sel)} onClick={() => onPick?.(n)}
        className={`relative flex flex-col items-start justify-between overflow-hidden border border-white/50 p-0.5 text-left leading-none transition-colors ${cls}`}
        aria-label={s ? `Square ${n}, taken by ${s.label}` : `Square ${n}, available`} data-testid={`stb-square-${n}`}>
        <span className={`text-[9px] font-bold sm:text-[11px] ${s && !sel ? "text-lime-300" : "text-white [text-shadow:0_1px_2px_#000]"}`}>{tag(n)}</span>
        {s && showOwners && <span className="w-full truncate text-[8px] font-semibold text-white sm:text-[10px]">{s.status === "held" ? "…" : s.label}</span>}
      </button>
    );
  };
  const ball = revealed && game === "spot" && (
    <div className="pointer-events-none absolute" data-testid="stb-ball"
      style={{ width: "9%", left: `${((revealed.square - 1) % 10) * 10 + 0.5}%`, top: `${Math.floor((revealed.square - 1) / 10) * (100 / rows) + (100 / rows - 13) / 2}%` }}>
      <Ball />
    </div>
  );
  return (
    <div className="relative">
      {game === "squares" && <p className="mb-1 text-xs font-bold text-slate-600" data-testid="stb-squares-legend">{dg ? `Each square shows ${card.home_team} last digit – ${card.away_team} last digit` : `The numbers (${card.home_team} – ${card.away_team} last digits) are dealt at kick-off`}</p>}
      <div className="overflow-auto border-2 border-slate-900 bg-slate-900" style={{ touchAction: "pan-x pan-y pinch-zoom" }} data-testid="stb-board">
        <div className="relative" style={{ width: `${zoom * 100}%` }}>
          <img src={sceneUrl(card.image)} alt="Spot the ball – where is the ball?" className="block w-full select-none" draggable={false} />
          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: "repeat(10, 1fr)", gridTemplateRows: `repeat(${rows}, 1fr)` }}>
            {Array.from({ length: card.squares }, (_, i) => cell(i + 1))}
          </div>
          {ball}
        </div>
      </div>
      <button type="button" onClick={() => setZoom((z) => (z === 1 ? 2 : 1))} className="absolute right-2 top-2 flex items-center gap-1 border-2 border-slate-900 bg-white px-2 py-1 text-xs font-bold shadow" data-testid="stb-zoom-button">
        {zoom === 1 ? <><ZoomIn className="h-4 w-4" /> Zoom</> : <><ZoomOut className="h-4 w-4" /> Fit</>}
      </button>
    </div>
  );
};
